import React, { useRef } from 'react'

import { DATE_FORMAT } from '@/constants/common.constant'
import { AgGridCellValueMethod } from '@/enums/axiom-report.enum'
import { ISecurity } from '@/interfaces/portfolioOptimization.interface'
import { getUpdatedCellValue } from '@/services/portfolio-optimization/optimization-reports.service'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import { customDateComparator, gridDateFormatter } from '@/utils/tableGridUtils'
import {
  ColDef,
  GridOptions,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Box, Typography } from '@mui/material'
import { CandidateScreenSecuritiesColumns } from './CandidateScreenSecuritiesColumns'

type ScreenCriteriaGridProps = {
  securitiesList: ISecurity[]
  securitiesFetching: boolean
  selectedPortfolioDate: string
}

export const CandidateScreenSecuritiesGrid: React.FC<
  ScreenCriteriaGridProps
> = ({ securitiesList, securitiesFetching, selectedPortfolioDate }) => {
  // Grid Configuration
  const gridRef = useRef<AgGridReact>(null)

  const gridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    noRowsOverlayComponentParams: () => {
      return {
        noRowsMessage: 'No securities available.',
      }
    },
    rowSelection: 'single' as const,
    columnDefs: [
      ...CandidateScreenSecuritiesColumns.map((item) => {
        if (item.cellDataType === 'number') {
          ;(item.valueGetter = (params: ValueGetterParams) =>
            getUpdatedCellValue(
              params.data,
              params.colDef.field as string,
              AgGridCellValueMethod.VALUE_GETTER,
            )),
            (item.valueFormatter = (params: ValueFormatterParams) =>
              getUpdatedCellValue(
                params.data,
                params.colDef.field as string,
                AgGridCellValueMethod.VALUE_FORMATTER,
              ))
        } else if (item.cellDataType === 'date') {
          ;(item.tooltipField = undefined),
            (item.valueFormatter = (params: ValueFormatterParams) =>
              gridDateFormatter(params, DATE_FORMAT.TABLE_CHART)),
            (item.filterParams = {
              comparator: customDateComparator,
              buttons: ['reset'],
            }),
            (item.tooltipValueGetter = (params: ITooltipParams) =>
              gridDateFormatter(params, DATE_FORMAT.TABLE_CHART))
        }
        return item
      }),
    ] as ColDef[],
  } as GridOptions

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h5'>
          Screening Date: {selectedPortfolioDate}
        </Typography>
        <Typography variant='h5'>
          Current number of securities in subset: {securitiesList.length}
        </Typography>
      </Box>

      <Box
        className='ag-theme-quartz screening-securities-table'
        boxSizing='border-box'
      >
        <AgGridReact
          ref={gridRef}
          rowData={securitiesList}
          gridOptions={gridOptions}
          data-cy='screen-securities-ag-grid'
          loading={securitiesFetching}
        ></AgGridReact>
      </Box>
    </>
  )
}
